import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Megaphone, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const departments = {
  rh: {
    label: "Recursos Humanos",
    levels: {
      basico: [
        "Elaboración de planillas de la nómina",
        "Elaboración de planillas de la CCSS",
        "Elaboración de planillas del INS",
        "Amonestaciones",
        "Seguimiento de incapacidades",
        "Control de Vacaciones",
        "Aguinaldos",
      ],
      medio: [
        "Apoyo virtual en contrataciones",
        "Apoyo virtual en despidos y elaboración de documentación requerida",
        "Cálculo de liquidaciones por despido o renuncia",
      ],
      premium: [],
    },
  },
  conta: {
    label: "Contabilidad",
    levels: {
      basico: ["Contabilidad tributaria", "Detalle de ingresos y gastos tributarios", "Asesoría básica"],
      medio: ["Detalle de ingresos y gastos contable (tributario y bancario)", "Controles bancarios"],
      premium: [
        "Estado financiero completo anual",
        "1 flujo proyectado mensual",
        "Asesoría premium",
        "Asesoría de proyectos personalizada",
      ],
    },
  },
  legal: {
    label: "Servicios Jurídicos y Asesoría Laboral",
    levels: {
      basico: [
        "Elaboración de contratos de trabajo",
        "4 horas de asesoría virtual mensual",
        "Composiciones accionarias digitales ilimitadas",
      ],
      medio: [
        "6 horas de asesoría virtual mensual",
        "Elaboración de contratos de arrendamiento",
        "1 visita mensual al centro de trabajo para asesoría",
        "1 audiencia de conciliación en el MTSS",
        "Revisión de documentos para trámites",
      ],
      premium: [
        "8 horas de asesoría virtual mensual",
        "Descuento especial en trámites notariales",
        "5 composiciones accionarias impresas mensuales",
      ],
    },
  },
};

const marketingBase = [
  "Creación de contenido mensual (diseño gráfico)",
  "Elaboración de estrategia de marketing",
  "Reuniones virtuales para dar seguimiento a la estrategia",
  "Visita mensual a la empresa para generar material de trabajo*",
];

const marketingGeneral = [...marketingBase, "Control y gestión de redes sociales"];

const marketingRubros = {
  "Básico": [
    ...marketingBase,
    { text: "Control y gestión de redes sociales", sub: ["Hasta 4 publicaciones mensuales", "Hasta 2 reels mensuales", "Hasta 8 historias mensuales"] },
  ],
  "Full": [
    ...marketingBase,
    { text: "Control y gestión de redes sociales", sub: ["Hasta 6 publicaciones mensuales", "Hasta 4 reels mensuales", "Hasta 12 historias mensuales"] },
  ],
};

const marketingNote = "*Aplican condiciones";

const marketing = {
  name: "Marketing Digital · Diseño Gráfico",
  prices: [
    { label: "Básico", price: "¢80.000" },
    { label: "Full", price: "¢115.000" },
  ],
};

const levelOrder = ["basico", "medio", "premium"];
const levelName = { basico: "Básico", medio: "Medio", premium: "Premium" };

const rhPremiumPresencial = {
  "Apoyo virtual en contrataciones": "Apoyo presencial en contrataciones*",
  "Apoyo virtual en despidos y elaboración de documentación requerida": "Apoyo presencial en despidos y elaboración de documentación requerida*",
};

function cumulativeRubros(deptKey, level) {
  const dept = departments[deptKey];
  const idx = levelOrder.indexOf(level);
  let items = [];
  for (let i = 0; i <= idx; i++) items = items.concat(dept.levels[levelOrder[i]] || []);
  return items;
}

const packages = [
  { id: "basico", name: "Básico", level: "basico", depts: ["rh", "conta", "legal"], fisica: "¢245.000", juridica: "¢260.000" },
  { id: "medio", name: "Medio", level: "medio", depts: ["rh", "conta", "legal"], fisica: "¢320.000", juridica: "¢335.000" },
  { id: "premium", name: "Premium", level: "premium", depts: ["rh", "conta", "legal"], fisica: "¢405.000", juridica: "¢420.000", highlight: true },
  { id: "full1", name: "Full 1", level: "premium", depts: ["rh", "conta", "legal"], marketing: "Básico", fisica: "¢480.000", juridica: "¢495.000" },
  { id: "full2", name: "Full 2", level: "premium", depts: ["rh", "conta", "legal"], marketing: "Full", fisica: "¢510.000", juridica: "¢525.000", highlight: true },
];

const fade = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function FeatureList({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((f) => {
        const text = typeof f === "string" ? f : f.text;
        const sub = typeof f === "string" ? null : f.sub;
        return (
          <li key={text} className="text-gray-300 text-sm leading-relaxed">
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 h-5 w-5 rounded-full bg-vteal/20 flex items-center justify-center shrink-0 p-0.5">
                <Check className="h-3 w-3 text-vteal-light" />
              </span>
              {text}
            </div>
            {sub && (
              <ul className="mt-1.5 ml-[30px] space-y-1">
                {sub.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-gray-400 text-[13px] leading-relaxed">
                    <span className="mt-[7px] h-1 w-1 rounded-full bg-vteal-light/60 shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Chip({ label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-1.5 text-left text-sm rounded-full border border-vteal-light/30 text-gray-200 hover:bg-vteal-light/10 hover:text-white px-3.5 py-1.5 transition-colors"
    >
      <Eye className="h-3.5 w-3.5 text-vteal-light" /> {label}
    </button>
  );
}

export const PlanesSuscripcion = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deptModal, setDeptModal] = useState(null); // {title, items}

  const openDept = (deptKey, level) => {
    let items = cumulativeRubros(deptKey, level);
    let note = deptKey === "rh" ? ["Los precios aplican hasta para 50 trabajadores."] : [];
    if (deptKey === "rh" && level === "premium") {
      items = items.map((it) => rhPremiumPresencial[it] || it);
      note.push("*Aplican condiciones");
    }
    setDeptModal({
      title: `${departments[deptKey].label} · Nivel ${levelName[level]}`,
      items,
      note,
    });
  };
  const openMarketing = (variant) =>
    setDeptModal({ title: `Marketing Digital · ${variant}`, items: marketingRubros[variant], note: [marketingNote] });

  const carouselRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const hoverPause = useRef(false);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    let raf;
    const step = () => {
      if (!hoverPause.current && !drag.current.down) {
        el.scrollLeft += 0.75;
        const half = el.scrollWidth / 2;
        if (half > 0 && el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const normalizeScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    const half = el.scrollWidth / 2;
    if (half <= 0) return;
    if (el.scrollLeft >= half) el.scrollLeft -= half;
    else if (el.scrollLeft < 0) el.scrollLeft += half;
  };

  const onCarouselDown = (e) => {
    drag.current = { down: true, startX: e.clientX, startScroll: carouselRef.current.scrollLeft, moved: false };
  };
  const onCarouselMove = (e) => {
    if (!drag.current.down) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    carouselRef.current.scrollLeft = drag.current.startScroll - dx;
    normalizeScroll();
  };
  const onCarouselUp = () => { drag.current.down = false; };
  const onCarouselClick = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <section id="planes" className="relative py-28 sm:py-36 bg-vnavy-800 grain overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-vteal-light" />
            <span className="text-sm uppercase tracking-[0.3em] text-vteal-light font-semibold">Suscripción Mensual</span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-white leading-tight">
            Planes de suscripción mensual
          </h2>
          <p className="mt-6 text-gray-400 leading-relaxed">
            Elija Marketing por separado o combine con los departamentos que necesite en un solo paquete.
          </p>
        </div>

        {/* Marketing primero */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-xl bg-vteal/15 flex items-center justify-center">
              <Megaphone className="h-5 w-5 text-vteal-light" />
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl text-white">{marketing.name}</h3>
          </div>
          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-50px" }}
            className="rounded-2xl bg-vnavy border border-vteal-light/30 p-8 grid md:grid-cols-2 gap-8 shadow-[0_0_30px_rgba(20,184,166,0.08)]"
          >
            <div>
              <FeatureList items={marketingGeneral} />
              <p className="text-gray-600 text-[11px] mt-3">*Aplican condiciones</p>
            </div>
            <div className="flex flex-col justify-center gap-4 md:border-l md:border-white/10 md:pl-8">
              {marketing.prices.map((p) => (
                <div key={p.label} className="flex items-center justify-between gap-3">
                  <div className="flex items-baseline gap-2">
                    <span className="text-vteal-light text-sm uppercase tracking-[0.15em] font-semibold">{p.label}</span>
                    <span className="font-serif text-2xl text-white tracking-tight">{p.price}</span>
                  </div>
                  <button
                    onClick={() => setSelectedPlan(`${marketing.name} · ${p.label}`)}
                    data-testid={`plan-request-btn-marketing-${p.label}`}
                    className="rounded-full bg-vteal-light px-5 py-2 text-vnavy font-semibold text-sm hover:bg-white transition-colors"
                  >
                    Solicitar información
                  </button>
                </div>
              ))}
              <p className="text-gray-600 text-[11px] uppercase tracking-[0.15em]">Precios por mes · 13% IVA no incluido</p>
            </div>
          </motion.div>
        </div>

        {/* Paquetes combinados */}
        <div>
          <h3 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-white leading-tight">Paquetes combinados</h3>
          <p className="mt-6 mb-8 text-gray-400 leading-relaxed">Recursos Humanos + Contabilidad + Servicios Jurídicos y Asesoría Laboral. Full incluye además Marketing Digital.</p>

          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-50px" }}
            ref={carouselRef}
            onMouseEnter={() => (hoverPause.current = true)}
            onMouseLeave={() => { hoverPause.current = false; onCarouselUp(); }}
            onMouseDown={onCarouselDown}
            onMouseMove={onCarouselMove}
            onMouseUp={onCarouselUp}
            onClickCapture={onCarouselClick}
            className="carousel-viewport overflow-x-auto py-2 cursor-grab active:cursor-grabbing select-none [mask-image:linear-gradient(to_right,transparent,black_48px,black_calc(100%-48px),transparent)]"
            data-testid="packages-carousel"
          >
            <div className="flex w-max">
              {["a", "b"].flatMap((copy) =>
                packages.map((pkg) => (
                  <div
                    key={`${pkg.id}-${copy}`}
                    data-testid={`package-card-${pkg.id}-${copy}`}
                    className={`rounded-2xl p-6 flex flex-col w-[300px] sm:w-[340px] shrink-0 mr-6 ${
                      pkg.highlight
                        ? "bg-vnavy border border-vteal-light/40 shadow-[0_0_30px_rgba(20,184,166,0.1)]"
                        : "bg-vnavy border border-white/10"
                    }`}
                  >
                    <h4 className="font-serif text-2xl text-white mb-1">{pkg.name}</h4>
                    <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-5">Incluye · toque para ver detalle</p>

                    <div className="flex flex-wrap gap-2 mb-6 flex-1 content-start">
                      {pkg.depts.map((d) => (
                        <Chip
                          key={d}
                          label={departments[d].label}
                          testid={`dept-chip-${pkg.id}-${d}-${copy}`}
                          onClick={() => openDept(d, pkg.level)}
                        />
                      ))}
                      {pkg.marketing && (
                        <Chip
                          label={`Marketing ${pkg.marketing}`}
                          testid={`dept-chip-${pkg.id}-marketing-${copy}`}
                          onClick={() => openMarketing(pkg.marketing)}
                        />
                      )}
                    </div>

                    <div className="pt-5 border-t border-white/10 space-y-2">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-gray-400 text-sm">Persona física</span>
                        <span className="font-serif text-2xl text-white tracking-tight">{pkg.fisica}</span>
                      </div>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-gray-400 text-sm">Persona jurídica</span>
                        <span className="font-serif text-2xl text-white tracking-tight">{pkg.juridica}</span>
                      </div>
                      <p className="text-gray-600 text-[11px] uppercase tracking-[0.15em] pt-1">Precios por mes · 13% IVA no incluido</p>
                    </div>

                    <button
                      onClick={() => setSelectedPlan(`Paquete ${pkg.name}`)}
                      data-testid={`plan-request-btn-${pkg.id}-${copy}`}
                      className={`mt-5 w-full rounded-full px-6 py-3 font-semibold text-sm transition-colors duration-300 ${
                        pkg.highlight
                          ? "bg-vteal-light text-vnavy hover:bg-white"
                          : "border border-white/20 text-white hover:border-vteal-light hover:text-vteal-light"
                      }`}
                    >
                      Solicitar información
                    </button>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </div>

        <div className="mt-14 rounded-2xl border border-vteal-light/25 bg-vteal/[0.06] p-6 sm:p-8">
          <p className="text-white text-base sm:text-lg leading-relaxed">
            <span className="font-semibold text-vteal-light">¿Su empresa necesita algo a la medida?</span> Cada plan es totalmente flexible: lo ajustamos según sus necesidades y la cantidad de trabajadores de su empresa. Solicite información y le prepararemos una propuesta personalizada, sin compromiso.
          </p>
        </div>

        <PlanRequestDialog plan={selectedPlan} open={!!selectedPlan} onClose={() => setSelectedPlan(null)} />

        <Dialog open={!!deptModal} onOpenChange={(o) => !o && setDeptModal(null)}>
          <DialogContent className="bg-vnavy-800 border-white/10 text-white" data-testid="dept-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">{deptModal?.title}</DialogTitle>
              <DialogDescription className="text-gray-500">Rubros incluidos en este nivel</DialogDescription>
            </DialogHeader>
            <div className="py-2 max-h-[60vh] overflow-y-auto">
              {deptModal && <FeatureList items={deptModal.items} />}
              {deptModal?.note?.map((n) => (
                <p key={n} className="text-gray-500 text-xs mt-4">{n}</p>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

const emptyForm = { name: "", company: "", address: "", email: "", whatsapp: "", employees: "", referral: "" };

const referralOptions = [
  "Facebook",
  "Instagram",
  "Google (búsqueda en internet)",
  "Página web",
  "Anuncio o publicidad",
  "Recomendación de un amigo o conocido",
  "Ya soy cliente",
  "Otro",
];

function PlanRequestDialog({ plan, open, onClose }) {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (Object.values(form).some((v) => !String(v).trim())) {
      toast.error("Complete todos los espacios para enviar la solicitud.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/plan-requests", { plan, ...form });
      toast.success("¡Solicitud enviada! Le contactaremos pronto.");
      setForm(emptyForm);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar la solicitud.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-vnavy border-white/15 text-white placeholder:text-gray-600 focus-visible:ring-vteal-light";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-vnavy-800 border-white/10 text-white" data-testid="plan-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Solicitar información</DialogTitle>
          <DialogDescription className="text-vteal-light">{plan}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Nombre completo *</label>
            <Input data-testid="plan-req-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Su nombre" className={inputCls} />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Nombre de la empresa *</label>
            <Input data-testid="plan-req-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Su empresa" className={inputCls} />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Dirección *</label>
            <Input data-testid="plan-req-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Provincia, cantón, distrito" className={inputCls} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Correo electrónico *</label>
              <Input data-testid="plan-req-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="correo@empresa.com" className={inputCls} />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">WhatsApp *</label>
              <Input data-testid="plan-req-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+506 0000 0000" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Cantidad de trabajadores *</label>
            <Input data-testid="plan-req-employees" type="number" min="0" value={form.employees} onChange={(e) => set("employees", e.target.value)} placeholder="Ej. 12" className={inputCls} />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">¿Cómo nos conociste? *</label>
            <select
              data-testid="plan-req-referral"
              value={form.referral}
              onChange={(e) => set("referral", e.target.value)}
              className={`w-full h-9 rounded-md border border-white/15 bg-vnavy px-3 text-sm focus:outline-none focus:ring-2 focus:ring-vteal-light/50 ${form.referral ? "text-white" : "text-gray-500"}`}
            >
              <option value="" disabled>Seleccione una opción</option>
              {referralOptions.map((o) => (
                <option key={o} value={o} className="text-white">{o}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            data-testid="plan-req-submit"
            disabled={loading}
            className="w-full rounded-full bg-vteal-light px-8 py-3.5 text-vnavy font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Enviando..." : "Enviar solicitud"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
